import { css, html, LitElement, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import {
    TeqtankCommerce,
    type Commerce,
    type CommerceCart,
    type CommerceDataSource,
    type CommerceEventUnsubscribe,
    type CommerceProduct,
    type CommerceProductCategory,
    type CommerceProductSort,
    type CommerceProductVariant,
    type CommerceProductSummary,
    type SearchCommerceProductsRequest,
} from "@teqtank/commerce-sdk";
import { sanitizeCommerceHtml } from "./sanitize-commerce-html.js";

export interface TeqtankProductSelectedDetail {
    readonly product: CommerceProductSummary;
    readonly detailUrl: string;
}

export interface TeqtankProductQuickAddedDetail {
    readonly product: CommerceProductSummary;
    readonly cart: CommerceCart;
}

@customElement("teqtank-products")
export class TeqtankProducts extends LitElement {
    @property({ attribute: false }) public accessor commerce: Commerce | undefined;
    @property({ attribute: "commerce-key" }) public accessor commerceKey = "demo";
    @property({ attribute: "data-source" }) public accessor dataSource: CommerceDataSource =
        "playground";
    @property({ attribute: "page-size", type: Number }) public accessor pageSize = 12;
    @property({ attribute: "show-filters", type: Boolean }) public accessor showFilters = true;
    @property({ attribute: "show-search", type: Boolean }) public accessor showSearch = true;
    @property({ attribute: "show-sort", type: Boolean }) public accessor showSort = true;

    @state() private accessor products: readonly CommerceProductSummary[] = [];
    @state() private accessor categories: readonly CommerceProductCategory[] = [];
    @state() private accessor selectedCategoryId = "";
    @state() private accessor query = "";
    @state() private accessor sort: CommerceProductSort = "default";
    @state() private accessor loading = true;
    @state() private accessor loadingMore = false;
    @state() private accessor errorMessage = "";
    @state() private accessor actionErrorMessage = "";
    @state() private accessor paginationErrorMessage = "";
    @state() private accessor currentPage = 1;
    @state() private accessor totalItems = 0;
    @state() private accessor hasNextPage = false;
    @state() private accessor addingProductId = "";
    @state() private accessor quickViewProduct: CommerceProduct | undefined;
    @state() private accessor quickViewSelections: Readonly<Record<string, string>> = Object.freeze(
        {},
    );
    @state() private accessor quickViewPurchaseType: "one-time" | "recurring" = "one-time";

    private ownsCommerce = false;
    private searchVersion = 0;
    private searchController: AbortController | undefined;
    private searchDebounce: ReturnType<typeof setTimeout> | undefined;
    private marketCommerce: Commerce | undefined;
    private unsubscribeMarket: CommerceEventUnsubscribe | undefined;

    public static override styles = css`
        :host {
            --tt-ink: var(--teqtank-color-text, #16221d);
            --tt-muted: var(--teqtank-color-text-muted, #6b746e);
            --tt-accent: var(--teqtank-color-accent, #728f4f);
            --tt-surface: var(--teqtank-color-surface, #faf8f4);
            --tt-soft: var(--teqtank-color-surface-soft, #eee8df);
            --tt-line: var(--teqtank-color-border, rgba(22, 34, 29, 0.1));
            --tt-display: var(--teqtank-font-heading, Georgia, serif);
            --tt-body: var(--teqtank-font-body, Inter, ui-sans-serif, system-ui, sans-serif);
            position: relative;
            display: block;
            container-type: inline-size;
            color: var(--tt-ink);
            font-family: var(--tt-body);
        }

        * {
            box-sizing: border-box;
        }
        button,
        input,
        select {
            font: inherit;
        }
        button {
            cursor: pointer;
        }

        .shop {
            display: grid;
            grid-template-columns: 220px minmax(0, 1fr);
            gap: var(--teqtank-products-gap, clamp(1.5rem, 4cqw, 4rem));
            padding: clamp(1rem, 3cqw, 2.25rem);
            background: var(--tt-surface);
        }

        .filters {
            display: grid;
            align-content: start;
            gap: 0.55rem;
        }

        .filters h2 {
            margin: 0 0 0.55rem;
            font: 500 1.45rem var(--tt-display);
        }

        .filter-button {
            width: 100%;
            padding: 0.7rem 0;
            border: 0;
            border-bottom: 1px solid var(--tt-line);
            background: transparent;
            color: var(--tt-muted);
            text-align: left;
        }

        .filter-button.active {
            color: var(--tt-ink);
            font-weight: 800;
        }

        .results {
            min-width: 0;
        }

        .toolbar {
            margin-bottom: 1.25rem;
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 1rem;
        }

        .count {
            margin: 0;
            color: var(--tt-muted);
            font-size: 0.78rem;
        }

        .controls {
            display: flex;
            gap: 0.65rem;
        }

        input,
        select {
            min-width: 0;
            padding: 0.72rem 0.85rem;
            border: 1px solid var(--tt-line);
            background: color-mix(in srgb, var(--tt-surface) 88%, white);
            color: var(--tt-ink);
        }

        .grid {
            display: grid;
            grid-template-columns: var(--teqtank-products-columns, repeat(3, minmax(0, 1fr)));
            gap: var(--teqtank-products-gap, clamp(0.9rem, 2.2cqw, 1.5rem));
            align-items: stretch;
        }

        .category-groups {
            display: grid;
            gap: clamp(2.25rem, 5cqw, 4.5rem);
        }

        .category-group {
            min-width: 0;
        }

        .category-heading {
            margin: 0 0 clamp(1rem, 2cqw, 1.5rem);
            padding-bottom: 0.8rem;
            display: flex;
            align-items: baseline;
            justify-content: space-between;
            gap: 1rem;
            border-bottom: 1px solid var(--tt-line);
        }

        .category-heading h2 {
            margin: 0;
            font: 500 clamp(1.55rem, 3.5cqw, 2.2rem)/1.1 var(--tt-display);
        }

        .category-heading span {
            flex: 0 0 auto;
            color: var(--tt-muted);
            font-size: 0.72rem;
        }

        .pagination {
            margin-top: clamp(1.5rem, 3cqw, 2.5rem);
            display: grid;
            justify-items: center;
            gap: 0.75rem;
        }

        .load-more {
            min-height: 46px;
            padding: 0.75rem 1.4rem;
            border: 1px solid var(--tt-ink);
            border-radius: 999px;
            background: transparent;
            color: var(--tt-ink);
            font-size: 0.76rem;
            font-weight: 800;
        }

        .load-more:hover {
            background: var(--tt-ink);
            color: white;
        }
        .load-more:disabled {
            cursor: wait;
            opacity: 0.6;
        }

        .pagination-error {
            margin: 0;
            color: #8b2424;
            font-size: 0.78rem;
            text-align: center;
        }

        .action-error {
            margin: 0 0 1rem;
            padding: 0.85rem 1rem;
            border: 1px solid color-mix(in srgb, #8b2424 35%, transparent);
            border-radius: var(--teqtank-radius-small, 10px);
            background: color-mix(in srgb, #8b2424 7%, var(--tt-surface));
            color: #8b2424;
            font-size: 0.84rem;
            line-height: 1.45;
        }

        .card {
            min-width: 0;
            display: flex;
            flex-direction: column;
            overflow: hidden;
            border: 1px solid var(--tt-line);
            border-radius: var(--teqtank-products-card-radius, var(--teqtank-radius-medium, 20px));
            background: var(
                --teqtank-products-card-background,
                color-mix(in srgb, var(--tt-surface) 75%, white)
            );
            box-shadow: var(--teqtank-products-card-shadow, none);
            transition:
                transform var(--teqtank-motion-normal, 200ms) var(--teqtank-easing-standard, ease),
                box-shadow var(--teqtank-motion-normal, 200ms) var(--teqtank-easing-standard, ease),
                border-color var(--teqtank-motion-normal, 200ms)
                    var(--teqtank-easing-standard, ease);
        }

        .card:hover {
            transform: translateY(-5px);
            border-color: color-mix(in srgb, var(--tt-ink) 16%, transparent);
            box-shadow: 0 18px 45px rgba(20, 31, 26, 0.12);
        }

        .media {
            position: relative;
            aspect-ratio: var(--teqtank-products-media-aspect-ratio, 0.92);
            overflow: hidden;
            background: var(--tt-soft);
        }

        .media button {
            width: 100%;
            height: 100%;
            padding: 0;
            border: 0;
            background: transparent;
        }

        .media img {
            width: 100%;
            height: 100%;
            display: block;
            object-fit: var(--teqtank-product-image-fit, contain);
            transition: transform 0.32s ease;
        }

        .card:hover .media img {
            transform: scale(1.025);
        }

        .image-fallback {
            width: 100%;
            height: 100%;
            display: grid;
            place-items: center;
            padding: 1.5rem;
            color: var(--tt-accent);
            font: 500 clamp(1.5rem, 4cqw, 2.7rem)/1 var(--tt-display);
            text-align: center;
        }

        .badge {
            position: absolute;
            top: 14px;
            left: 14px;
            z-index: 1;
            max-width: calc(100% - 28px);
            padding: 0.35rem 0.55rem;
            border-radius: 999px;
            background: color-mix(in srgb, var(--tt-surface) 88%, transparent);
            color: var(--tt-ink);
            font-size: 0.65rem;
            font-weight: 800;
            white-space: nowrap;
        }

        .copy {
            flex: 1;
            padding: 20px;
            display: flex;
            flex-direction: column;
        }

        .meta {
            display: flex;
            justify-content: space-between;
            gap: 0.75rem;
            color: var(--tt-muted);
            font-size: 0.68rem;
            text-transform: uppercase;
            letter-spacing: 0.04em;
        }

        h3 {
            margin: 11px 0 8px;
            font: 600 19px/1.2 var(--tt-body);
        }

        .description {
            margin: 0 0 18px;
            color: var(--tt-muted);
            font-size: 13px;
            line-height: 1.55;
        }

        .card-content {
            display: grid;
            gap: 0.3rem;
            margin: -0.35rem 0 1rem;
        }

        .card-content-line {
            display: flex;
            flex-wrap: wrap;
            gap: 0.3rem;
            color: var(--tt-muted);
            font-size: 0.75rem;
            line-height: 1.4;
        }

        .card-content-label {
            color: var(--tt-ink);
            font-weight: 800;
        }
        .card-content-rich {
            width: 100%;
        }
        .card-content-rich > :first-child {
            margin-top: 0;
        }
        .card-content-rich > :last-child {
            margin-bottom: 0;
        }

        .foot {
            margin-top: auto;
            padding-top: 16px;
            display: flex;
            align-items: flex-end;
            justify-content: space-between;
            gap: 1rem;
            border-top: 1px solid var(--tt-line);
        }

        .price strong,
        .price small {
            display: block;
        }
        .price small {
            margin-top: 3px;
            color: var(--tt-muted);
            font-size: 0.66rem;
        }
        .price small b {
            color: var(--tt-accent);
        }

        .quick-add {
            min-height: 42px;
            padding: 10px 13px;
            display: inline-flex;
            align-items: center;
            gap: 9px;
            border: 1px solid var(--tt-ink);
            border-radius: 999px;
            background: var(--tt-ink);
            color: #fff;
            font-size: 0.7rem;
            font-weight: 800;
            white-space: nowrap;
        }

        .quick-add:hover {
            background: var(--tt-accent);
            border-color: var(--tt-accent);
        }
        .quick-add:disabled {
            opacity: 0.55;
            cursor: wait;
        }

        .quick-view-backdrop {
            position: fixed;
            inset: 0;
            z-index: 10000;
            display: grid;
            justify-items: end;
            background: rgba(16, 24, 20, 0.38);
        }

        .quick-view {
            position: relative;
            width: min(440px, 100cqw);
            max-width: 100%;
            height: 100%;
            overflow: auto;
            background: var(--tt-surface);
            box-shadow: -24px 0 70px rgba(20, 31, 26, 0.18);
        }

        .quick-view-copy {
            min-height: 100%;
            padding: clamp(2rem, 5cqw, 3rem) clamp(1.5rem, 4cqw, 2.5rem);
        }
        .quick-view-copy .eyebrow {
            margin: 0 0 0.55rem;
            color: var(--tt-accent);
            font-size: 0.68rem;
            font-weight: 800;
            letter-spacing: 0.12em;
            text-transform: uppercase;
        }
        .quick-view-copy h2 {
            margin: 0 0 0.75rem;
            font: 500 clamp(1.8rem, 5cqw, 2.7rem)/1.02 var(--tt-display);
        }
        .quick-view-copy > p {
            color: var(--tt-muted);
            line-height: 1.6;
        }

        .quick-close {
            position: absolute;
            top: 1rem;
            right: 1rem;
            z-index: 2;
            width: 2.5rem;
            height: 2.5rem;
            border: 1px solid var(--tt-line);
            border-radius: 50%;
            background: var(--tt-surface);
            color: var(--tt-ink);
            font-size: 1.2rem;
        }

        .quick-options {
            margin: 1.4rem 0;
        }
        .quick-option {
            margin: 0;
            padding: 1rem 0;
            border: 0;
            border-top: 1px solid var(--tt-line);
        }
        .quick-option legend {
            width: 100%;
            margin: 0 0 0.75rem;
            padding: 0;
            display: flex;
            align-items: center;
            gap: 0.55rem;
            font-size: 0.75rem;
            font-weight: 700;
        }
        .quick-option legend > span {
            width: 1.4rem;
            height: 1.4rem;
            display: inline-grid;
            place-items: center;
            border-radius: 50%;
            background: var(--tt-ink);
            color: white;
            font-size: 0.6rem;
        }
        .quick-option legend strong {
            margin-left: auto;
            color: var(--tt-accent);
        }
        .quick-option-values {
            display: flex;
            flex-wrap: wrap;
            gap: 0.5rem;
        }
        .quick-option-value {
            min-height: 40px;
            padding: 9px 15px;
            border: 1px solid var(--tt-line);
            border-radius: 999px;
            background: transparent;
            color: var(--tt-ink);
            font-size: 0.72rem;
        }
        .quick-option-value.active {
            border-color: var(--tt-ink);
            background: var(--tt-ink);
            color: white;
        }

        .quick-purchase {
            margin: 1rem 0;
            padding: 0.3rem;
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 0.3rem;
            border-radius: 12px;
            background: var(--tt-soft);
        }
        .quick-purchase button {
            padding: 0.8rem;
            border: 0;
            border-radius: 9px;
            background: transparent;
            color: var(--tt-ink);
            text-align: left;
        }
        .quick-purchase button.active {
            background: var(--tt-surface);
            box-shadow: 0 5px 20px rgba(20, 30, 25, 0.08);
        }
        .quick-purchase span,
        .quick-purchase strong,
        .quick-purchase small {
            display: block;
        }
        .quick-purchase span {
            font-size: 0.7rem;
        }
        .quick-purchase strong {
            margin-top: 0.2rem;
        }
        .quick-purchase small {
            margin-top: 0.2rem;
            color: var(--tt-muted);
            font-size: 0.62rem;
        }

        .quick-confirm {
            width: 100%;
            min-height: 48px;
            border: 0;
            border-radius: 999px;
            background: var(--tt-ink);
            color: white;
            font-weight: 800;
        }
        .quick-confirm:hover {
            background: var(--tt-accent);
        }

        .state {
            padding: 2rem;
            border: 1px solid var(--tt-line);
            background: var(--tt-surface);
            color: var(--tt-muted);
        }

        .error {
            color: #8b2424;
        }

        @container (max-width: 980px) {
            .shop {
                grid-template-columns: 1fr;
            }
            .filters {
                grid-template-columns: repeat(4, minmax(0, 1fr));
            }
            .filters h2 {
                grid-column: 1 / -1;
            }
            .filter-button {
                text-align: center;
            }
            .grid {
                grid-template-columns: repeat(2, minmax(0, 1fr));
            }
        }

        @container (max-width: 650px) {
            .shop {
                padding: 0.75rem;
            }
            .filters {
                display: flex;
                overflow-x: auto;
                padding-bottom: 0.35rem;
            }
            .filters h2 {
                display: none;
            }
            .filter-button {
                width: auto;
                flex: 0 0 auto;
                padding: 0.55rem 0.7rem;
                border: 1px solid var(--tt-line);
                border-radius: 999px;
            }
            .toolbar {
                align-items: stretch;
                flex-direction: column;
            }
            .controls {
                display: grid;
                grid-template-columns: 1fr;
            }
            .grid {
                grid-template-columns: 1fr;
            }
        }

        @container (max-width: 420px) {
            .copy {
                padding: 16px;
            }
            .foot {
                align-items: stretch;
                flex-direction: column;
            }
            .quick-add {
                justify-content: center;
                width: 100%;
            }
        }
    `;

    protected override updated(changed: Map<PropertyKey, unknown>): void {
        if (changed.has("commerce") || changed.has("pageSize")) {
            void this.refresh();
        }
    }

    public override disconnectedCallback(): void {
        this.searchController?.abort();
        this.searchController = undefined;
        if (this.searchDebounce !== undefined) clearTimeout(this.searchDebounce);
        this.searchDebounce = undefined;
        this.unsubscribeMarket?.();
        this.unsubscribeMarket = undefined;
        this.marketCommerce = undefined;
        if (this.ownsCommerce) {
            this.commerce?.dispose();
        }
        super.disconnectedCallback();
    }

    public async refresh(): Promise<void> {
        const searchVersion = ++this.searchVersion;
        this.searchController?.abort();
        const searchController = new AbortController();
        this.searchController = searchController;
        this.loading = true;
        this.loadingMore = false;
        this.errorMessage = "";
        this.paginationErrorMessage = "";

        try {
            const runtime = await this.requireCommerce();
            this.bindMarket(runtime);
            const [response, categories] = await Promise.all([
                runtime.products.search(this.createSearchRequest(1), {
                    signal: searchController.signal,
                }),
                runtime.products.getCategories(undefined, {
                    signal: searchController.signal,
                }),
            ]);

            if (searchVersion !== this.searchVersion) return;

            this.products = response.items;
            this.categories = categories;
            this.currentPage = response.pagination.page;
            this.totalItems = response.pagination.totalItems;
            this.hasNextPage = response.pagination.hasNextPage;
        } catch (error) {
            if (searchVersion !== this.searchVersion) return;

            this.products = [];
            this.currentPage = 1;
            this.totalItems = 0;
            this.hasNextPage = false;
            this.errorMessage =
                error instanceof Error ? error.message : "Products could not be loaded.";
        } finally {
            if (this.searchController === searchController) {
                this.searchController = undefined;
            }
            if (searchVersion === this.searchVersion) this.loading = false;
        }
    }

    protected override render() {
        if (this.loading) {
            return html`<div class="state" part="loading-state">Loading products…</div>`;
        }

        if (this.errorMessage.length > 0) {
            return html`<div class="state error" part="error-state" role="alert">
                ${this.errorMessage}
            </div>`;
        }

        return html`
            <section class="shop" part="products shell">
                ${this.showFilters ? this.renderFilters() : nothing}
                <div class="results" part="results">
                    ${this.showSearch || this.showSort ? this.renderToolbar() : nothing}
                    ${
                        this.actionErrorMessage.length === 0
                            ? nothing
                            : html`<p class="action-error" part="action-error" role="alert">
                                  ${this.actionErrorMessage}
                              </p>`
                    }
                    ${
                        this.products.length === 0
                            ? html`<div class="state" part="empty-state">
                                  No products match your selection.
                              </div>`
                            : html` ${this.renderProductResults()} ${this.renderPagination()} `
                    }
                </div>
            </section>
            ${this.quickViewProduct === undefined ? nothing : this.renderQuickView(this.quickViewProduct)}
        `;
    }

    private renderQuickView(product: CommerceProduct) {
        const variant = this.resolveQuickViewVariant(product);
        const offers = variant?.offers ?? product.representativeOffers;
        const oneTimeOffer = offers.find((offer) => offer.purchaseType === "one-time");
        const recurringOffer = offers.find((offer) => offer.purchaseType === "recurring");
        const activeOffer =
            offers.find((offer) => offer.purchaseType === this.quickViewPurchaseType) ??
            oneTimeOffer ??
            offers[0];
        const activeOptionGroups = this.activeQuickViewOptionGroups(product);

        return html`
            <div
                class="quick-view-backdrop"
                part="quick-view-backdrop"
                @click=${this.onQuickViewBackdrop}
            >
                <aside
                    class="quick-view"
                    part="quick-view"
                    role="dialog"
                    aria-modal="true"
                    aria-label=${`Quick add ${product.name}`}
                >
                    <button
                        class="quick-close"
                        part="quick-view-close"
                        type="button"
                        aria-label="Close quick add"
                        @click=${this.closeQuickView}
                    >
                        ×
                    </button>
                    <div class="quick-view-copy" part="quick-view-content">
                        <p class="eyebrow" part="eyebrow">${product.tags[0] ?? "Product"}</p>
                        <h2 part="quick-view-title">${product.name}</h2>
                        <p>${product.shortDescription}</p>
                        ${this.renderCardContent(product)}
                        <div class="quick-options">
                            ${activeOptionGroups.map((option, index) => {
                                const values = this.availableQuickViewValues(product, option.id);
                                const selected = values.find(
                                    (value) => value.id === this.quickViewSelections[option.id],
                                );
                                return html`
                                    <fieldset class="quick-option">
                                        <legend>
                                            <span>${index + 1}</span>Choose
                                            ${option.name.toLowerCase()}<strong
                                                >${selected?.label ?? ""}</strong
                                            >
                                        </legend>
                                        <div class="quick-option-values">
                                            ${values.map(
                                                (value) => html`
                                                    <button
                                                        class="quick-option-value ${selected?.id === value.id ? "active" : ""}"
                                                        type="button"
                                                        @click=${() => this.selectQuickViewOption(product, option.id, value.id)}
                                                    >
                                                        ${value.label}
                                                    </button>
                                                `,
                                            )}
                                        </div>
                                    </fieldset>
                                `;
                            })}
                        </div>
                        ${
                            recurringOffer === undefined
                                ? nothing
                                : html`
                                      <div class="quick-purchase">
                                          <button
                                              class=${this.quickViewPurchaseType === "one-time" ? "active" : ""}
                                              type="button"
                                              @click=${() => (this.quickViewPurchaseType = "one-time")}
                                          >
                                              <span>One-time</span
                                              ><strong
                                                  >${oneTimeOffer?.price.current.formatted ?? "Unavailable"}</strong
                                              >
                                          </button>
                                          <button
                                              class=${this.quickViewPurchaseType === "recurring" ? "active" : ""}
                                              type="button"
                                              @click=${() => (this.quickViewPurchaseType = "recurring")}
                                          >
                                              <span>Subscribe & save</span
                                              ><strong
                                                  >${recurringOffer.price.current.formatted}</strong
                                              >${recurringOffer.price.compareAt === undefined ? nothing : html`<small>Compared with ${recurringOffer.price.compareAt.formatted}</small>`}
                                          </button>
                                      </div>
                                  `
                        }
                        <button
                            class="quick-confirm"
                            part="quick-view-action primary-action"
                            type="button"
                            ?disabled=${variant === null || activeOffer === undefined || this.addingProductId === product.id}
                            @click=${() => this.addConfiguredProduct(product)}
                        >
                            ${this.addingProductId === product.id ? "Adding…" : `Add to bag${activeOffer === undefined ? "" : ` · ${activeOffer.price.current.formatted}`}`}
                        </button>
                    </div>
                </aside>
            </div>
        `;
    }

    private renderFilters() {
        return html`
            <aside class="filters" part="filters" aria-label="Product categories">
                <h2 part="filters-title">Shop by ritual</h2>
                <button
                    class="filter-button ${this.selectedCategoryId === "" ? "active" : ""}"
                    type="button"
                    @click=${() => this.selectCategory("")}
                >
                    All
                </button>
                ${this.categories.map(
                    (category) => html`
                        <button
                            class="filter-button ${this.selectedCategoryId === category.id ? "active" : ""}"
                            type="button"
                            @click=${() => this.selectCategory(category.id)}
                        >
                            ${category.name}
                        </button>
                    `,
                )}
            </aside>
        `;
    }

    private renderToolbar() {
        const count =
            this.products.length === this.totalItems
                ? `${this.totalItems} product${this.totalItems === 1 ? "" : "s"}`
                : `Showing ${this.products.length} of ${this.totalItems} products`;

        return html`
            <div class="toolbar" part="toolbar">
                <p class="count" part="result-count" aria-live="polite">${count}</p>
                <div class="controls" part="controls">
                    ${this.showSearch ? html`<input type="search" placeholder="Search rituals" .value=${this.query} @input=${this.onSearch} />` : nothing}
                    ${
                        this.showSort
                            ? html`
                                  <select .value=${this.sort} @change=${this.onSort}>
                                      <option value="default">Featured</option>
                                      <option value="price-ascending">Price: low to high</option>
                                      <option value="price-descending">Price: high to low</option>
                                      <option value="name-ascending">Name: A to Z</option>
                                      <option value="newest">Newest</option>
                                  </select>
                              `
                            : nothing
                    }
                </div>
            </div>
        `;
    }

    private renderProductResults() {
        if (this.selectedCategoryId.length > 0 || this.categories.length === 0) {
            return this.renderProductGrid(this.products, true);
        }

        const assignedProductIds = new Set<string>();
        const groups = this.categories.flatMap((category) => {
            const products = this.products.filter((product) =>
                product.categoryIds.includes(category.id),
            );

            if (products.length === 0) return [];
            products.forEach((product) => assignedProductIds.add(product.id));
            return [{ category, products }];
        });
        const uncategorizedProducts = this.products.filter(
            (product) => !assignedProductIds.has(product.id),
        );

        return html`
            <div class="category-groups" part="category-groups" id="product-grid">
                ${groups.map(({ category, products }) =>
                    this.renderCategoryGroup(category.name, category.id, products),
                )}
                ${
                    uncategorizedProducts.length === 0
                        ? nothing
                        : this.renderCategoryGroup(
                              "More products",
                              "uncategorized",
                              uncategorizedProducts,
                          )
                }
            </div>
        `;
    }

    private renderCategoryGroup(
        name: string,
        categoryId: string,
        products: readonly CommerceProductSummary[],
    ) {
        const headingId = `category-${categoryId}`;
        return html`
            <section class="category-group" part="category-group" aria-labelledby=${headingId}>
                <div class="category-heading" part="category-heading">
                    <h2 id=${headingId} part="category-title">${name}</h2>
                    <span part="category-count"
                        >${products.length} product${products.length === 1 ? "" : "s"}</span
                    >
                </div>
                ${this.renderProductGrid(products)}
            </section>
        `;
    }

    private renderProductGrid(
        products: readonly CommerceProductSummary[],
        isPaginationTarget = false,
    ) {
        return html`
            <div
                class="grid"
                part="product-list"
                id=${isPaginationTarget ? "product-grid" : nothing}
            >
                ${products.map((product) => this.renderProduct(product))}
            </div>
        `;
    }

    private renderPagination() {
        if (!this.hasNextPage && this.paginationErrorMessage.length === 0) return nothing;

        return html`
            <div class="pagination" part="pagination">
                ${
                    this.hasNextPage
                        ? html`
                              <button
                                  class="load-more"
                                  part="load-more secondary-action"
                                  type="button"
                                  aria-controls="product-grid"
                                  ?disabled=${this.loadingMore}
                                  @click=${this.loadMore}
                              >
                                  ${this.loadingMore ? "Loading more…" : "Load more products"}
                              </button>
                          `
                        : nothing
                }
                ${
                    this.paginationErrorMessage.length === 0
                        ? nothing
                        : html`<p class="pagination-error" part="pagination-error" role="alert">
                              ${this.paginationErrorMessage}
                          </p>`
                }
            </div>
        `;
    }

    private renderProduct(product: CommerceProductSummary) {
        const oneTimeOffer =
            product.representativeOffers.find((offer) => offer.purchaseType === "one-time") ??
            product.representativeOffers[0];
        const category = this.categories.find((item) => product.categoryIds.includes(item.id));
        const recurringOffer = product.representativeOffers.find(
            (offer) => offer.purchaseType === "recurring",
        );

        return html`
            <article class="card" part="product-card">
                <div class="media" part="product-media">
                    ${product.tags[0] === undefined ? nothing : html`<span class="badge" part="product-badge">${product.tags[0]}</span>`}
                    <button
                        type="button"
                        aria-label=${`View ${product.name}`}
                        @click=${() => this.openDetail(product)}
                    >
                        ${
                            product.primaryImage === null
                                ? html`<span class="image-fallback">${product.name}</span>`
                                : html`<img
                                      src=${product.primaryImage.url}
                                      alt=${product.primaryImage.alternativeText}
                                      loading="lazy"
                                      decoding="async"
                                  />`
                        }
                    </button>
                </div>
                <div class="copy" part="product-content">
                    <div class="meta" part="product-meta">
                        <span>${category?.name ?? product.tags[0] ?? "Wellness"}</span
                        ><span
                            >${recurringOffer === undefined ? "One-time" : "Subscribe & save"}</span
                        >
                    </div>
                    <h3 part="product-title">${product.name}</h3>
                    <p class="description" part="product-description">
                        ${product.shortDescription}
                    </p>
                    ${this.renderCardContent(product)}
                    <div class="foot" part="product-footer">
                        <div class="price" part="product-price">
                            <strong
                                >${oneTimeOffer?.price.current.formatted ?? "Unavailable"}
                                <span>one-time</span></strong
                            >
                            ${recurringOffer === undefined ? nothing : html`<small><span>${recurringOffer.price.current.formatted} subscribed</span>${recurringOffer.price.compareAt === undefined ? nothing : html` · <b>compare at ${recurringOffer.price.compareAt.formatted}</b>`}</small>`}
                        </div>
                        <button
                            class="quick-add"
                            part="product-action primary-action"
                            type="button"
                            ?disabled=${this.addingProductId === product.id}
                            @click=${() => this.quickAdd(product)}
                        >
                            ${this.addingProductId === product.id ? "Adding…" : "Quick add +"}
                        </button>
                    </div>
                </div>
            </article>
        `;
    }

    private renderCardContent(product: CommerceProductSummary) {
        const content = product.cardContent ?? [];
        return content.length === 0
            ? nothing
            : html` <div class="card-content" part="product-card-content">
                  ${content.map(
                      (line) =>
                          html`<div class="card-content-line" part="product-card-content-line">
                              ${line.label === undefined ? nothing : html`<span class="card-content-label">${line.label}</span>`}
                              ${
                                  line.format === "rich-html"
                                      ? html`<div class="card-content-rich" part="rich-content">
                                            ${unsafeHTML(sanitizeCommerceHtml(line.value, { allowLinks: false }))}
                                        </div>`
                                      : html`<span>${line.value}</span>`
                              }
                          </div>`,
                  )}
              </div>`;
    }

    private async quickAdd(product: CommerceProductSummary): Promise<void> {
        this.addingProductId = product.id;
        this.actionErrorMessage = "";

        try {
            const runtime = await this.requireCommerce();

            // TRANSITION(PERF-003): Gateways predating the optimized summary do not
            // declare whether configuration is required. Remove this detail fallback
            // after 40Plus migrates to the new Platform contract.
            if (product.requiresConfiguration === undefined) {
                const detail = await runtime.products.getById(product.id);

                if (detail.optionGroups.length > 0) {
                    this.openQuickView(detail);
                    return;
                }

                const cart = await runtime.cart.add({
                    productId: detail.id,
                    variantId: detail.defaultVariantId,
                    quantity: 1,
                    purchaseType: "one-time",
                });

                this.dispatchProductAdded(product, cart);
                return;
            }

            if (product.requiresConfiguration) {
                const detail = await runtime.products.getById(product.id);
                this.openQuickView(detail);
                return;
            }

            const cart = await runtime.cart.add({
                productId: product.id,
                quantity: 1,
                purchaseType: "one-time",
            });

            this.dispatchProductAdded(product, cart);
        } catch (error) {
            this.actionErrorMessage = this.describeActionError(error);
        } finally {
            this.addingProductId = "";
        }
    }

    private openQuickView(product: CommerceProduct): void {
        const selections: Record<string, string> = {};
        const defaultVariant = product.variants.find(
            (variant) => variant.id === product.defaultVariantId,
        );

        for (const option of product.optionGroups) {
            const values = this.availableQuickViewValues(product, option.id, selections);
            const preferred = defaultVariant?.selections.find(
                (selection) => selection.optionId === option.id,
            )?.valueId;
            const selected = values.find((value) => value.id === preferred) ?? values[0];
            if (selected !== undefined) selections[option.id] = selected.id;
        }

        this.quickViewSelections = Object.freeze(selections);
        this.quickViewPurchaseType = "one-time";
        this.quickViewProduct = product;
    }

    private readonly closeQuickView = (): void => {
        this.quickViewProduct = undefined;
        this.quickViewSelections = Object.freeze({});
    };

    private readonly onQuickViewBackdrop = (event: Event): void => {
        if (event.target === event.currentTarget) this.closeQuickView();
    };

    private availableQuickViewValues(
        product: CommerceProduct,
        optionId: string,
        selections: Readonly<Record<string, string>> = this.quickViewSelections,
    ) {
        const option = product.optionGroups.find((candidate) => candidate.id === optionId);
        if (option === undefined) return [];
        if (option.dependsOnOptionId === undefined) return option.values;
        const parentValueId = selections[option.dependsOnOptionId];
        if (parentValueId === undefined) return [];
        return option.values.filter(
            (value) => value.availableForValueIds?.includes(parentValueId) === true,
        );
    }

    private selectQuickViewOption(
        product: CommerceProduct,
        optionId: string,
        valueId: string,
    ): void {
        const selections: Record<string, string> = {
            ...this.quickViewSelections,
            [optionId]: valueId,
        };
        const changedIndex = product.optionGroups.findIndex((option) => option.id === optionId);

        for (let index = changedIndex + 1; index < product.optionGroups.length; index += 1) {
            const option = product.optionGroups[index];
            if (option === undefined) continue;
            const values = this.availableQuickViewValues(product, option.id, selections);
            if (!values.some((value) => value.id === selections[option.id])) {
                const first = values[0];
                if (first === undefined) delete selections[option.id];
                else selections[option.id] = first.id;
            }
        }

        this.quickViewSelections = Object.freeze(selections);
    }

    private resolveQuickViewVariant(product: CommerceProduct): CommerceProductVariant | null {
        const activeOptionIds = this.activeQuickViewOptionGroups(product).map(
            (option) => option.id,
        );
        return (
            product.variants.find(
                (variant) =>
                    variant.selections.length === activeOptionIds.length &&
                    variant.selections.every(
                        (selection) =>
                            this.quickViewSelections[selection.optionId] === selection.valueId,
                    ) &&
                    activeOptionIds.every((optionId) =>
                        variant.selections.some((selection) => selection.optionId === optionId),
                    ),
            ) ?? null
        );
    }

    private activeQuickViewOptionGroups(
        product: CommerceProduct,
        selections: Readonly<Record<string, string>> = this.quickViewSelections,
    ) {
        return product.optionGroups.filter(
            (option) => this.availableQuickViewValues(product, option.id, selections).length > 0,
        );
    }

    private async addConfiguredProduct(product: CommerceProduct): Promise<void> {
        const variant = this.resolveQuickViewVariant(product);
        if (variant === null) return;
        this.addingProductId = product.id;
        this.actionErrorMessage = "";

        try {
            const runtime = await this.requireCommerce();
            const offer = variant.offers.find(
                (candidate) => candidate.purchaseType === this.quickViewPurchaseType,
            );
            const frequency = offer?.frequency?.options.find(
                (candidate) => candidate.id === offer.frequency?.defaultOptionId,
            );
            const cart = await runtime.cart.add({
                productId: product.id,
                variantId: variant.id,
                quantity: 1,
                purchaseType: this.quickViewPurchaseType,
                ...(frequency === undefined
                    ? {}
                    : { frequencyId: frequency.id, frequencyDays: frequency.days }),
            });
            const summary = this.products.find((candidate) => candidate.id === product.id);
            if (summary !== undefined) this.dispatchProductAdded(summary, cart);
            this.closeQuickView();
        } catch (error) {
            this.actionErrorMessage = this.describeActionError(error);
        } finally {
            this.addingProductId = "";
        }
    }

    private dispatchProductAdded(product: CommerceProductSummary, cart: CommerceCart): void {
        this.dispatchEvent(
            new CustomEvent<TeqtankProductQuickAddedDetail>("teqtank-product-added", {
                detail: Object.freeze({ product, cart }),
                bubbles: true,
                composed: true,
            }),
        );
    }

    private async openDetail(product: CommerceProductSummary): Promise<void> {
        const runtime = await this.requireCommerce();
        const detailUrl = runtime.products.getDetailUrl(product);
        const event = new CustomEvent<TeqtankProductSelectedDetail>("teqtank-product-selected", {
            detail: Object.freeze({ product, detailUrl }),
            bubbles: true,
            composed: true,
            cancelable: true,
        });

        if (this.dispatchEvent(event)) {
            globalThis.location.assign(detailUrl);
        }
    }

    private readonly loadMore = async (): Promise<void> => {
        if (this.loading || this.loadingMore || !this.hasNextPage) return;

        const searchVersion = this.searchVersion;
        this.loadingMore = true;
        this.paginationErrorMessage = "";

        try {
            const runtime = await this.requireCommerce();
            const response = await runtime.products.search(
                this.createSearchRequest(this.currentPage + 1),
            );

            if (searchVersion !== this.searchVersion) return;

            const existingIds = new Set(this.products.map((product) => product.id));
            this.products = Object.freeze([
                ...this.products,
                ...response.items.filter((product) => !existingIds.has(product.id)),
            ]);
            this.currentPage = response.pagination.page;
            this.totalItems = response.pagination.totalItems;
            this.hasNextPage = response.pagination.hasNextPage;
        } catch (error) {
            if (searchVersion !== this.searchVersion) return;

            this.paginationErrorMessage =
                error instanceof Error ? error.message : "More products could not be loaded.";
        } finally {
            if (searchVersion === this.searchVersion) this.loadingMore = false;
        }
    };

    private createSearchRequest(page: number): SearchCommerceProductsRequest {
        return {
            ...(this.query.trim().length === 0 ? {} : { query: this.query.trim() }),
            ...(this.selectedCategoryId.length === 0
                ? {}
                : { categoryId: this.selectedCategoryId }),
            sort: this.sort,
            page,
            pageSize: this.pageSize,
        };
    }

    private selectCategory(categoryId: string): void {
        this.selectedCategoryId = categoryId;
        void this.refresh();
    }

    private onSearch(event: Event): void {
        this.query = (event.currentTarget as HTMLInputElement).value;
        if (this.searchDebounce !== undefined) clearTimeout(this.searchDebounce);
        this.searchDebounce = setTimeout(() => {
            this.searchDebounce = undefined;
            void this.refresh();
        }, 250);
    }

    private onSort(event: Event): void {
        this.sort = (event.currentTarget as HTMLSelectElement).value as CommerceProductSort;
        void this.refresh();
    }

    private async requireCommerce(): Promise<Commerce> {
        if (this.commerce !== undefined) {
            return this.commerce;
        }

        this.commerce = await TeqtankCommerce.initialize({
            commerceKey: this.commerceKey,
            dataSource: this.dataSource,
        });
        this.ownsCommerce = true;
        return this.commerce;
    }

    private describeActionError(error: unknown): string {
        return error instanceof Error
            ? error.message
            : "The product could not be added to your bag. Please try again.";
    }

    private bindMarket(runtime: Commerce): void {
        if (this.marketCommerce === runtime) return;
        this.unsubscribeMarket?.();
        this.marketCommerce = runtime;
        this.unsubscribeMarket = runtime.market.onChanged(() => {
            this.selectedCategoryId = "";
            this.quickViewProduct = undefined;
            void this.refresh();
        });
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "teqtank-products": TeqtankProducts;
    }
}
